import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Loader2, ShieldCheck, X, Award, ScrollText } from "lucide-react";
import { toast } from "sonner";
import {
  adminApproveCertificate,
  adminListPendingReviews,
  adminRejectCourseSubmission,
} from "@/lib/api/academy.functions";

export const Route = createFileRoute("/_authenticated/portal/admin/academy")({
  component: AdminAcademyReview,
  head: () => ({ meta: [{ title: "Academy review · Admin · NDLCS" }] }),
});

function AdminAcademyReview() {
  const qc = useQueryClient();
  const fetchList = useServerFn(adminListPendingReviews);
  const approve = useServerFn(adminApproveCertificate);
  const reject = useServerFn(adminRejectCourseSubmission);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-academy-reviews"],
    queryFn: () => fetchList(),
  });
  const approveMut = useMutation({
    mutationFn: (progressId: string) => approve({ data: { progressId } }),
    onSuccess: () => {
      toast.success("Certificate issued");
      qc.invalidateQueries({ queryKey: ["admin-academy-reviews"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not approve"),
  });

  const [rejecting, setRejecting] = useState<{ id: string; note: string } | null>(null);
  const rejectMut = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      reject({ data: { progressId: id, note } }),
    onSuccess: () => {
      toast.success("Submission rejected");
      setRejecting(null);
      qc.invalidateQueries({ queryKey: ["admin-academy-reviews"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not reject"),
  });

  const items = (data?.items ?? []) as any[];
  const pending = items.filter((i) => i.status === "awaiting_review");
  const recent = items.filter((i) => i.status !== "awaiting_review");

  return (
    <section className="space-y-6">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Admin · Academy</p>
        <h2 className="mt-1 font-display text-2xl text-navy">
          Course completions awaiting certificate sign-off
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Only admins can issue NDLCS Academy certificates. Approve to issue a
          signed, QR-verifiable certificate, or reject with a note.
        </p>
      </header>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-widest text-navy">
          Pending ({pending.length})
        </h3>
        {pending.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing waiting.</p>
        ) : (
          <ul className="space-y-3">
            {pending.map((it) => (
              <li key={it.id} className="rounded-lg border border-input bg-card p-4">
                <Row item={it} />
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    onClick={() => approveMut.mutate(it.id)}
                    disabled={approveMut.isPending}
                    className="inline-flex items-center gap-1.5 rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-navy-foreground hover:bg-navy/90 disabled:opacity-60"
                  >
                    {approveMut.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                    <Award className="h-3 w-3" /> Issue certificate
                  </button>
                  <button
                    onClick={() => setRejecting({ id: it.id, note: "" })}
                    className="inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-800 hover:bg-red-100"
                  >
                    <X className="h-3 w-3" /> Reject
                  </button>
                </div>

                {rejecting && rejecting.id === it.id && (
                  <div className="mt-3 rounded-md border border-red-300 bg-red-50 p-3">
                    <textarea
                      value={rejecting!.note}
                      onChange={(e) => setRejecting({ id: it.id, note: e.target.value })}
                      placeholder="Reason for rejection (visible to learner)"
                      className="w-full rounded-md border border-input bg-white p-2 text-xs"
                      rows={3}
                    />
                    <div className="mt-2 flex gap-2">
                      <button
                        disabled={rejectMut.isPending || rejecting!.note.trim().length < 3}
                        onClick={() => rejectMut.mutate({ id: it.id, note: rejecting!.note })}
                        className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                      >
                        Confirm rejection
                      </button>
                      <button
                        onClick={() => setRejecting(null)}
                        className="rounded-md border border-input px-3 py-1.5 text-xs"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-widest text-navy">
          Recent decisions
        </h3>
        {recent.length === 0 ? (
          <p className="text-sm text-muted-foreground">No recent decisions.</p>
        ) : (
          <ul className="space-y-2">
            {recent.map((it) => (
              <li key={it.id} className="rounded-md border border-border bg-muted/30 p-3">
                <Row item={it} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function Row({ item }: { item: any }) {
  const c = item.course_catalog ?? {};
  const p = item.profiles ?? {};
  const badge =
    item.status === "certified"
      ? "bg-emerald-100 text-emerald-800"
      : item.status === "rejected"
      ? "bg-red-100 text-red-800"
      : "bg-amber-100 text-amber-800";
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="min-w-0">
        <p className="font-display text-base text-navy">
          {p.full_name || p.email || item.user_id} · <span className="text-muted-foreground">{p.student_number || ""}</span>
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          <ScrollText className="mr-1 inline h-3 w-3" />
          {c.code ? `${c.code} — ${c.title}` : item.course_slug}
          {item.final_score != null && <> · final {item.final_score}%</>}
        </p>
      </div>
      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${badge}`}>
        <ShieldCheck className="mr-1 inline h-3 w-3" />
        {item.status.replace("_", " ")}
      </span>
    </div>
  );
}
